import { Spinner, Switch, useToast } from '@chakra-ui/react'
import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import DefaultModal from '../../../../../../components/DefaultModal'
import Toast from '../../../../../../components/Toast'
import {
  useCreateFeeStructureMutation,
  useGetCurrenciesQuery,
  useUpdateFeeStructureMutation,
} from '../../../../../../redux/services/coreApi'
import type {
  CreateCommand as FeeStructureCreateCommand,
  FeeStructureDto,
} from '../../../../../../types/coreApi-types'

interface CreateFeeStructureModalProps {
  isOpen: boolean
  onClose: () => void
  feeStructure?: FeeStructureDto | null
  onCreated?: (feeStructureId: string) => void
  onUpdated?: (feeStructureId: string) => void
}

type BaseFeeInput = {
  tempId: string
  eurAmountRequired: string
  feePercent: string
}

type BaseFeeCollectionInput = {
  tempId: string
  payConstrained: boolean
  receiveConstrained: boolean
  applicablePayCurrencyIds: string[]
  applicableReceiveCurrencyIds: string[]
  baseFees: BaseFeeInput[]
  isStepped: boolean
}

type BankFeeInput = {
  tempId: string
  feePercent: string
  currencyIds: string[]
}

const createTempId = () => Math.random().toString(36).slice(2)

const createBaseFeeInput = (): BaseFeeInput => ({
  tempId: createTempId(),
  eurAmountRequired: '',
  feePercent: '',
})

const createBaseFeeCollection = (): BaseFeeCollectionInput => ({
  tempId: createTempId(),
  payConstrained: false,
  receiveConstrained: false,
  applicablePayCurrencyIds: [],
  applicableReceiveCurrencyIds: [],
  baseFees: [createBaseFeeInput()],
  isStepped: false,
})

const createBankFee = (): BankFeeInput => ({
  tempId: createTempId(),
  feePercent: '',
  currencyIds: [],
})

const CreateFeeStructureModal: React.FC<CreateFeeStructureModalProps> = ({
  isOpen,
  onClose,
  feeStructure,
  onCreated,
  onUpdated,
}) => {
  const toast = useToast()
  const { data: currencies } = useGetCurrenciesQuery()
  const [createFeeStructure, { isLoading: isCreating }] =
    useCreateFeeStructureMutation()
  const [updateFeeStructure, { isLoading: isUpdating }] =
    useUpdateFeeStructureMutation()

  const isLoading = isCreating || isUpdating
  const isUpdateMode = !!feeStructure

  const [description, setDescription] = useState('')
  const [thirtyDayRolling, setThirtyDayRolling] = useState(false)
  const [baseFeeCollections, setBaseFeeCollections] = useState<
    BaseFeeCollectionInput[]
  >([createBaseFeeCollection()])
  const [bankFees, setBankFees] = useState<BankFeeInput[]>([])

  const resetForm = () => {
    setDescription('')
    setThirtyDayRolling(false)
    setBaseFeeCollections([createBaseFeeCollection()])
    setBankFees([])
  }

  const initializeForm = () => {
    if (!feeStructure) {
      resetForm()
      return
    }

    setDescription(feeStructure.description)
    setThirtyDayRolling(feeStructure.thirtyDayRolling)

    // Initialize base fee collections
    const collections: BaseFeeCollectionInput[] =
      feeStructure.baseFeeCollections.map(collection => ({
        tempId: createTempId(),
        payConstrained: collection.payConstrained,
        receiveConstrained: collection.receiveConstrained,
        applicablePayCurrencyIds:
          collection.applicablePayCurrencies?.map(c => c.id) ?? [],
        applicableReceiveCurrencyIds:
          collection.applicableReceiveCurrencies?.map(c => c.id) ?? [],
        isStepped: collection.isStepped,
        baseFees: collection.baseFees.map(fee => ({
          tempId: createTempId(),
          eurAmountRequired: fee.eurAmountRequired.toString(),
          feePercent: fee.feePercent.toString(),
        })),
      }))

    setBaseFeeCollections(
      collections.length > 0 ? collections : [createBaseFeeCollection()]
    )

    // Initialize bank fees
    const fees: BankFeeInput[] = feeStructure.bankFees.map(bankFee => ({
      tempId: createTempId(),
      feePercent: bankFee.feePercent.toString(),
      currencyIds: bankFee.currencies?.map(c => c.id) ?? [],
    }))

    setBankFees(fees)
  }

  useEffect(() => {
    if (isOpen && feeStructure) {
      initializeForm()
    } else if (!isOpen) {
      resetForm()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, feeStructure?.id])

  const currencyOptions = useMemo(() => currencies ?? [], [currencies])

  const updateCollection = (
    collectionId: string,
    updater: (collection: BaseFeeCollectionInput) => BaseFeeCollectionInput
  ) => {
    setBaseFeeCollections(prev =>
      prev.map(collection =>
        collection.tempId === collectionId ? updater(collection) : collection
      )
    )
  }

  const handleBaseFeeChange = (
    collectionId: string,
    feeId: string,
    key: keyof BaseFeeInput,
    value: string
  ) => {
    updateCollection(collectionId, collection => ({
      ...collection,
      baseFees: collection.baseFees.map(fee =>
        fee.tempId === feeId ? { ...fee, [key]: value } : fee
      ),
    }))
  }

  const handleCurrencyMultiSelect = (
    collectionId: string,
    key: 'applicablePayCurrencyIds' | 'applicableReceiveCurrencyIds',
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selected = Array.from(event.target.selectedOptions).map(
      option => option.value
    )
    updateCollection(collectionId, collection => ({
      ...collection,
      [key]: selected,
    }))
  }

  const handleBankFeeCurrencyChange = (
    bankFeeId: string,
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selected = Array.from(event.target.selectedOptions).map(
      option => option.value
    )
    setBankFees(prev =>
      prev.map(bankFee =>
        bankFee.tempId === bankFeeId
          ? {
              ...bankFee,
              currencyIds: selected,
            }
          : bankFee
      )
    )
  }

  const validateForm = () => {
    if (!description.trim()) {
      toast({
        position: 'top-right',
        render: () => (
          <Toast type="error">Description is required to continue.</Toast>
        ),
      })
      return false
    }

    if (baseFeeCollections.length === 0) {
      toast({
        position: 'top-right',
        render: () => (
          <Toast type="error">
            Add at least one base fee collection to continue.
          </Toast>
        ),
      })
      return false
    }

    for (const collection of baseFeeCollections) {
      if (collection.baseFees.length === 0) {
        toast({
          position: 'top-right',
          render: () => (
            <Toast type="error">
              Each collection requires at least one base fee tier.
            </Toast>
          ),
        })
        return false
      }

      if (
        collection.payConstrained &&
        collection.applicablePayCurrencyIds.length === 0
      ) {
        toast({
          position: 'top-right',
          render: () => (
            <Toast type="error">
              Select applicable pay currencies for constrained collections.
            </Toast>
          ),
        })
        return false
      }

      if (
        collection.receiveConstrained &&
        collection.applicableReceiveCurrencyIds.length === 0
      ) {
        toast({
          position: 'top-right',
          render: () => (
            <Toast type="error">
              Select applicable receive currencies for constrained collections.
            </Toast>
          ),
        })
        return false
      }

      for (const fee of collection.baseFees) {
        const eurAmount = Number.parseFloat(fee.eurAmountRequired)
        const feePercent = Number.parseFloat(fee.feePercent)
        if (
          Number.isNaN(eurAmount) ||
          Number.isNaN(feePercent) ||
          eurAmount < 0 ||
          feePercent <= 0 ||
          feePercent > 100
        ) {
          toast({
            position: 'top-right',
            render: () => (
              <Toast type="error">
                Base fee tiers must include valid amounts (≥ 0) and percentages
                (0-100].
              </Toast>
            ),
          })
          return false
        }
      }
    }

    for (const bankFee of bankFees) {
      const feePercent = Number.parseFloat(bankFee.feePercent)
      if (
        Number.isNaN(feePercent) ||
        feePercent < 0 ||
        feePercent > 100 ||
        bankFee.currencyIds.length === 0
      ) {
        toast({
          position: 'top-right',
          render: () => (
            <Toast type="error">
              Bank fees require a valid percentage (0-100) and at least one
              currency.
            </Toast>
          ),
        })
        return false
      }
    }

    return true
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    const payload: FeeStructureCreateCommand = {
      description: description.trim(),
      thirtyDayRolling,
      baseFeeCollections: baseFeeCollections.map(collection => ({
        payConstrained: collection.payConstrained,
        receiveConstrained: collection.receiveConstrained,
        applicablePayCurrencyIds: collection.applicablePayCurrencyIds,
        applicableReceiveCurrencyIds: collection.applicableReceiveCurrencyIds,
        isStepped: collection.isStepped,
        baseFees: collection.baseFees
          .sort(
            (a, b) =>
              Number.parseFloat(a.eurAmountRequired) -
              Number.parseFloat(b.eurAmountRequired)
          )
          .map(baseFee => ({
            eurAmountRequired: Number.parseFloat(baseFee.eurAmountRequired),
            feePercent: Number.parseFloat(baseFee.feePercent),
          })),
      })),
      bankFees: bankFees.map(bankFee => ({
        feePercent: Number.parseFloat(bankFee.feePercent),
        currencyIds: bankFee.currencyIds,
      })),
    }

    try {
      if (isUpdateMode && feeStructure) {
        const result = await updateFeeStructure({
          id: feeStructure.id,
          ...payload,
        }).unwrap()
        toast({
          position: 'top-right',
          render: () => (
            <Toast type="success">Fee structure updated successfully.</Toast>
          ),
        })
        onClose()
        resetForm()
        if (result?.id && onUpdated) {
          onUpdated(result.id)
        }
      } else {
        const result = await createFeeStructure(payload).unwrap()
        toast({
          position: 'top-right',
          render: () => (
            <Toast type="success">Fee structure created successfully.</Toast>
          ),
        })
        onClose()
        resetForm()
        if (result?.id && onCreated) {
          onCreated(result.id)
        }
      }
    } catch (error) {
      toast({
        position: 'top-right',
        render: () => (
          <Toast type="error">
            Failed to {isUpdateMode ? 'update' : 'create'} the fee structure.
            Please try again.
          </Toast>
        ),
      })
    }
  }

  const renderCurrencySelect = (
    selectedIds: string[],
    onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void,
    placeholder: string
  ) => (
    <select
      multiple
      className="input-card min-h-[140px]"
      value={selectedIds}
      onChange={onChange}
    >
      <option value="" disabled>
        {placeholder}
      </option>
      {currencyOptions.map(currency => (
        <option key={currency.id} value={currency.id}>
          {currency.name} ({currency.isoCode})
        </option>
      ))}
    </select>
  )

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={onClose}
      isLoading={isLoading}
      isLoadingText={
        isUpdateMode ? 'Updating fee structure...' : 'Creating fee structure...'
      }
      title={isUpdateMode ? 'Update Fee Structure' : 'Create Fee Structure'}
      maxW="900px"
    >
      <div className="p-6 pt-0 space-y-6 max-h-[70vh] overflow-y-auto text-brand-light-text-default">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            <label className="text-description text-sm" htmlFor="description">
              Description
            </label>
            <input
              id="description"
              type="text"
              className="input-card"
              placeholder="Describe this fee structure"
              value={description}
              onChange={event => setDescription(event.target.value)}
            />
          </div>
          <div className="flex items-center gap-3 pt-6 md:pt-8">
            <Switch
              isChecked={thirtyDayRolling}
              onChange={event => setThirtyDayRolling(event.target.checked)}
            />
            <span className="text-sm text-brand-light-text-default">
              30-day rolling performance window
            </span>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h3 className="text-lg font-semibold text-brand-light-text-default">
              Base fee collections
            </h3>
            <button
              type="button"
              className="text-brand-accent font-medium text-sm"
              onClick={() =>
                setBaseFeeCollections(prev => [
                  ...prev,
                  createBaseFeeCollection(),
                ])
              }
            >
              + Add collection
            </button>
          </div>

          {baseFeeCollections.map((collection, index) => (
            <div key={collection.tempId} className="card space-y-4">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-brand-light-text-default">
                  Collection {index + 1}
                </p>
                {baseFeeCollections.length > 1 && (
                  <button
                    type="button"
                    className="text-sm text-red-500 font-medium"
                    onClick={() =>
                      setBaseFeeCollections(prev =>
                        prev.filter(item => item.tempId !== collection.tempId)
                      )
                    }
                  >
                    Remove
                  </button>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <label className="flex items-center gap-3 text-sm text-brand-light-text-default">
                  <Switch
                    isChecked={collection.payConstrained}
                    onChange={event =>
                      updateCollection(collection.tempId, current => ({
                        ...current,
                        payConstrained: event.target.checked,
                        applicablePayCurrencyIds: event.target.checked
                          ? current.applicablePayCurrencyIds
                          : [],
                      }))
                    }
                  />
                  Pay currency constrained
                </label>

                <label className="flex items-center gap-3 text-sm text-brand-light-text-default">
                  <Switch
                    isChecked={collection.receiveConstrained}
                    onChange={event =>
                      updateCollection(collection.tempId, current => ({
                        ...current,
                        receiveConstrained: event.target.checked,
                        applicableReceiveCurrencyIds: event.target.checked
                          ? current.applicableReceiveCurrencyIds
                          : [],
                      }))
                    }
                  />
                  Receive currency constrained
                </label>
              </div>

              <div className="flex items-center gap-3 text-sm text-brand-light-text-default">
                <Switch
                  isChecked={collection.isStepped}
                  onChange={event =>
                    updateCollection(collection.tempId, current => ({
                      ...current,
                      isStepped: event.target.checked,
                    }))
                  }
                />
                <div className="flex flex-col">
                  <span className="font-medium">Stepped fee structure</span>
                  <span className="text-xs text-description">
                    {collection.isStepped
                      ? 'Fees step directly from one bracket to another'
                      : 'Fees interpolate linearly between brackets'}
                  </span>
                </div>
              </div>

              {collection.payConstrained && (
                <div className="space-y-2">
                  <label className="text-sm text-description">
                    Select pay currencies
                  </label>
                  {renderCurrencySelect(
                    collection.applicablePayCurrencyIds,
                    event =>
                      handleCurrencyMultiSelect(
                        collection.tempId,
                        'applicablePayCurrencyIds',
                        event
                      ),
                    'Choose pay currencies'
                  )}
                  <p className="text-description text-xs">
                    Hold Ctrl/⌘ to select multiple currencies.
                  </p>
                </div>
              )}

              {collection.receiveConstrained && (
                <div className="space-y-2">
                  <label className="text-sm text-description">
                    Select receive currencies
                  </label>
                  {renderCurrencySelect(
                    collection.applicableReceiveCurrencyIds,
                    event =>
                      handleCurrencyMultiSelect(
                        collection.tempId,
                        'applicableReceiveCurrencyIds',
                        event
                      ),
                    'Choose receive currencies'
                  )}
                  <p className="text-description text-xs">
                    Hold Ctrl/⌘ to select multiple currencies.
                  </p>
                </div>
              )}

              <div className="space-y-3 bg-brand-light-background rounded-xl p-4">
                <p className="text-sm font-medium text-brand-light-text-default">
                  Base fee tiers
                </p>
                {collection.baseFees.map((fee, feeIndex) => (
                  <div
                    key={fee.tempId}
                    className="grid md:grid-cols-2 gap-3 items-end"
                  >
                    <div className="flex flex-col gap-2">
                      <label className="text-xs text-description uppercase tracking-wide">
                        EUR amount required
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="input-card"
                        value={fee.eurAmountRequired}
                        onChange={event =>
                          handleBaseFeeChange(
                            collection.tempId,
                            fee.tempId,
                            'eurAmountRequired',
                            event.target.value
                          )
                        }
                        placeholder="0.00"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-xs text-description uppercase tracking-wide">
                        Fee percent
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          className="input-card flex-1"
                          value={fee.feePercent}
                          onChange={event =>
                            handleBaseFeeChange(
                              collection.tempId,
                              fee.tempId,
                              'feePercent',
                              event.target.value
                            )
                          }
                          placeholder="0.00"
                        />
                        <span className="text-description text-sm">%</span>
                      </div>
                    </div>

                    {collection.baseFees.length > 1 && (
                      <div className="md:col-span-2 flex justify-end">
                        <button
                          type="button"
                          className="text-xs text-red-500 font-medium"
                          onClick={() =>
                            updateCollection(collection.tempId, current => ({
                              ...current,
                              baseFees: current.baseFees.filter(
                                item => item.tempId !== fee.tempId
                              ),
                            }))
                          }
                        >
                          Remove tier
                        </button>
                      </div>
                    )}
                  </div>
                ))}

                <button
                  type="button"
                  className="text-brand-accent text-sm font-medium"
                  onClick={() =>
                    updateCollection(collection.tempId, current => ({
                      ...current,
                      baseFees: [...current.baseFees, createBaseFeeInput()],
                    }))
                  }
                >
                  + Add base fee tier
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h3 className="text-lg font-semibold text-brand-light-text-default">
              Bank fees
            </h3>
            <button
              type="button"
              className="text-brand-accent font-medium text-sm"
              onClick={() => setBankFees(prev => [...prev, createBankFee()])}
            >
              + Add bank fee
            </button>
          </div>

          {bankFees.length === 0 ? (
            <p className="text-description text-sm">
              Optional: add bank-specific fees for selected currencies.
            </p>
          ) : (
            bankFees.map((bankFee, index) => (
              <div key={bankFee.tempId} className="card space-y-4 bg-white">
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-brand-light-text-default">
                    Bank fee {index + 1}
                  </p>
                  <button
                    type="button"
                    className="text-sm text-red-500 font-medium"
                    onClick={() =>
                      setBankFees(prev =>
                        prev.filter(item => item.tempId !== bankFee.tempId)
                      )
                    }
                  >
                    Remove
                  </button>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-xs text-description uppercase tracking-wide">
                    Fee percent
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      className="input-card flex-1 bg-white"
                      value={bankFee.feePercent}
                      onChange={event =>
                        setBankFees(prev =>
                          prev.map(item =>
                            item.tempId === bankFee.tempId
                              ? { ...item, feePercent: event.target.value }
                              : item
                          )
                        )
                      }
                      placeholder="0.00"
                    />
                    <span className="text-description text-sm">%</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm text-description">
                    Applicable currencies
                  </label>
                  <select
                    multiple
                    className="input-card min-h-[140px] bg-white"
                    value={bankFee.currencyIds}
                    onChange={event =>
                      handleBankFeeCurrencyChange(bankFee.tempId, event)
                    }
                  >
                    <option value="" disabled>
                      Select currencies
                    </option>
                    {currencyOptions.map(currency => (
                      <option key={currency.id} value={currency.id}>
                        {currency.name} ({currency.isoCode})
                      </option>
                    ))}
                  </select>
                  <p className="text-description text-xs">
                    Bank fees require at least one currency.
                  </p>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            className="button !bg-brand-light-card-border !text-brand-light-text-default"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="button"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? (
              <Spinner size="sm" />
            ) : isUpdateMode ? (
              'Update fee structure'
            ) : (
              'Create fee structure'
            )}
          </button>
        </div>
      </div>
    </DefaultModal>
  )
}

export default CreateFeeStructureModal
